import type { AbstractPlayableContent, TemporalIndicators } from "../playable/models/AbstractPlayableContent";
import { Division } from "../playable/models/Division";
import * as Tone from "tone";
import { SynthKeys } from "./models/SynthKeys";
import { synthController } from "./SynthController";
import { TinyEmitter } from "tiny-emitter";
import Vue from "vue";
import { playerConfig } from "../player/models/PlayerConfig";
import * as Audio from "../audio"
import { type AudioPlayerState, type AudioTimelinePartEventVoices, type MetaTimelinePartEvent, type TimelinePartEvents, type TimelinePartEvent, defaultAudioPlayerState } from "./models/AudioPlayerTypes";
import { RhythmicBloc } from "../playable/models/RhythmicBloc";
import { analyticsController } from "../user/analytics/AnalyticsController";
import {playerModals} from "src/modules/player/models/PlayerModals";

const MinuteInSeconds = 60
const SecondInMiliseconds = 1000
const LookAHead = 0.2
export const LookAHeadInMilliseconds = LookAHead * SecondInMiliseconds

// Use Tone.getTransport() as a base
class AudioPlayer extends TinyEmitter {

  // TODO: could be something simpler to do with that :
  // The transport emits the events: “start”, “stop”, “pause”, and “loop” which are called with the time of that event as the argument.
  public readonly state = Vue.observable<AudioPlayerState>({ ...defaultAudioPlayerState })

  private tonePart: Tone.Part | null = null;
  private playableContent: AbstractPlayableContent | null = null;
  private divisions: Record<number, Division> | null = null
  private countdownValue = 4
  private countdownEvent: TimelinePartEvent | null = null
  private divisionOffset = 0

  public isFirstCountdownTick = true

  get countdown() {
    return this.countdownValue
  }

  constructor() {
    super();

    // For some reason need to use lambda here to keep context from some views (eg: PlayerTimerController). should investigate
    this.on('play', (withCountdown: boolean = true) => this.play(withCountdown));
    this.on('pause', () => this.pause());
    this.on('stop', () => this.stop());

    this.on('setTimer', (time: number, snap: boolean = false, withOffset = true) => this.setTimer(time, snap, withOffset));
    this.on('setTimerToLoopStart', () => this.setTimerToLoopStart());
    this.on('loopRestart', (delay: number = this.RESTART_DELAY_MS) => this.restartFromLoop(delay));

    this.on('setCurrentDivision', (division: Division | null) => {
      if ((this.state.playerStatus === 'pause' || this.state.playerStatus === 'player_controlled') && division != null) {
        this.state.currentDivision = Division.divisionAsNextHandprintDivisionForPiano(division)
      }
      else if (this.state.playerStatus === 'countdown' && this.divisions && this.countdownEvent) {
        this.state.currentDivision = Division.divisionAsNextHandprintDivisionForPiano(this.divisions[this.countdownEvent.divisionId])
      }
      else {
        this.state.currentDivision = division
      }

      this.emit('onDivisionChange', this.state.currentDivision)
    })

    this.on('setTempoFromSpeed', (value) => this.setTempoFromSpeed(value))

    // Quite a specific one, only called for sound notification in case restart should be automatic
    // Work only on top of delayedstart when loopRestart has been called
    this.on('increaseLoopRestartDelayIfNeeded', (delay: number = this.RESTART_DELAY_MS) => this.increaseLoopRestartDelayIfNeeded(delay));

    this.on('clearTimeouts', () => this.clearTimeouts())
  }

  /* New Play */

  public initPlayableContent(playableContent: AbstractPlayableContent, _divisions: Division[], withCountdownDivisions = true) {
    try {
      this.state.isLoading = true
      this.resetPlayableContent();
      synthController.initSynth();

      this.setupTimeSignature(playableContent.temporalIndicators);
      this.initTempo(playableContent.temporalIndicators.tempo, playerConfig.state.speed);
      Tone.getTransport().seconds = 0
      this.setCountdown(playableContent.temporalIndicators.beatPerMeasure)
      this.playableContent = playableContent;
      const divisions = this.computeDivisions(_divisions, playableContent, withCountdownDivisions)
      const partArray = this.computePartArray(divisions, playableContent);
      this.tonePart = new Tone.Part((time, division) => this.playPartDivision(time, division), partArray).start(0);
      Tone.getContext().lookAhead = LookAHead;
      // To keep, just a quick remember on time values if needed
      // console.log('BPM : ' + Tone.getTransport().bpm.value + ' - soit un beat = ' + 60 / Tone.getTransport().bpm.value + ' secondes');
      // console.log('Measure : ' + Tone.getTransport()Time('1m').toSeconds() + ' ' + Tone.getTransport()Time('1m').toBarsBeatsSixteenths());
      // console.log('blanche : ' + Tone.getTransport()Time('2n').toSeconds() + ' ' + Tone.getTransport()Time('2n').toBarsBeatsSixteenths()); // equal 1n ? why ?
      // console.log('noir pointé (ternary beat): ' + Tone.getTransport()Time('4n.').toSeconds() + ' ' + Tone.getTransport()Time('4n.').toBarsBeatsSixteenths());
      // console.log('noir (binary beat) : ' + Tone.getTransport()Time('4n').toSeconds() + ' ' + Tone.getTransport()Time('4n').toBarsBeatsSixteenths());
      // console.log('croche : ' + Tone.getTransport()Time('8n').toSeconds() + ' ' + Tone.getTransport()Time('8n').toBarsBeatsSixteenths());
      // console.log('double : ' + Tone.getTransport()Time('16n').toSeconds() + ' ' + Tone.getTransport()Time('16n').toBarsBeatsSixteenths());

      // Tone.getTransport().scheduleRepeat((time) => {
      //   console.log('Current Measure : ' + Tone.getTransport()Time(time).toBarsBeatsSixteenths());
      //   // synthController.playMelody(new SynthKeys([66], [1]), time)
      // }, "1m");
      // Tone.getTransport().scheduleRepeat((time) => {
      //   console.log('Current Binary Beat : ' + Tone.getTransport()Time(time).toBarsBeatsSixteenths());
      // //    synthController.playMelody(new SynthKeys([44], [1]), time)
      // }, "4n");
      // Tone.getTransport().scheduleRepeat((time) => {
      //   console.log('Current Ternary Beat : ' + Tone.getTransport()Time(time).toBarsBeatsSixteenths());
      //   // synthController.playPiano(new SynthKeys([44], [1]), time)
      // }, "4n.");
    }
    catch (error) {
      this.triggerError(error, 'initPlayableContent()')
    }
    finally {
      this.state.isLoading = false
    }
  }
  private setupTimeSignature(temporalIndicators: TemporalIndicators) {
    this.state.divisionPerBeat = temporalIndicators.divisionPerBeat;
    this.state.beatPerMeasure = temporalIndicators.beatPerMeasure;
    const isTernary = temporalIndicators.divisionPerBeat === 3

    const unit = isTernary ? 8 : 4
    const unitCount = isTernary ? (temporalIndicators.beatPerMeasure * 3) : temporalIndicators.beatPerMeasure;

    Tone.getTransport().timeSignature = [unitCount, unit];
    // Set the PPQ (Pulses Per Quarter Note) to align with our custom divisions.
    // Multiplying 'divisionPerBeat' by 96 ensures a high-resolution timing mechanism.
    // The value 96 is a standard MIDI resolution, providing precise timing for complex rhythms.
    // This approach allows each division to correspond to an integer number of ticks, simplifying timing calculations.
    Tone.getTransport().PPQ = temporalIndicators.divisionPerBeat * 96;
  }

  /**
   * TODO: could simplify this function
   *       For audio
   *          by precomputing objects like synthKeys
   *          by having soundParameters in local AudioPlayer instead of vuex store and without any compute for volume
   *          by having only one event by division ?  (not sure it's really useful)
   *          by having metronome not working has event but has schedule repeat in Tone.JS
   *
   * @param time
   * @param division
   * @private
   */
  private playPartDivision(time: number, division: TimelinePartEvents) {
    try {
      division.events.forEach((event) => {
        // console.log('dispatch : ' + event.type + ' at ' + time + ' for division ' + event.divisionId);
        if (event.type === 'audio' && this.state.playerStatus !== 'countdown') {
          const soundParameters = playerConfig.state.soundParameters;
          if (soundParameters.actives.MAIN && Audio.musicTranscriptionController.state.workerState !== 'ready') {
            if (event.voice === 'melody' && soundParameters.actives.MELODY) {
              const volume = soundParameters.actives.MELODY ? soundParameters.volumes.MELODY / 100 : 0;
              synthController.playMelody(new SynthKeys(event.key, event.durations), volume, time);
            }
            else if (event.voice === 'piano_left' && soundParameters.actives.LEFT) {
              const volume = soundParameters.actives.LEFT ? soundParameters.volumes.LEFT / 100 : 0;
              synthController.playPiano(new SynthKeys(event.key, event.durations), volume, time);
            }
            else if (event.voice === 'piano_right' && soundParameters.actives.RIGHT) {
              const volume = soundParameters.actives.RIGHT ? soundParameters.volumes.RIGHT / 100 : 0;
              synthController.playPiano(new SynthKeys(event.key, event.durations), volume, time);
            }
          }
        }
        else if (event.type === 'metronome') {
          if (playerConfig.state.soundParameters.actives.METRONOME || this.state.playerStatus === 'countdown') {
            synthController.playMetronome(time, playerConfig.state.soundParameters.volumes.METRONOME);
          }

          // decrement countdown ?
          if (this.state.playerStatus === 'countdown' && !this.isFirstCountdownTick) {
            this.setCountdown(this.countdown - 1)
          }
          else {
            this.isFirstCountdownTick = false
          }
        }
        else if (event.type === 'low_rate_refresh') {
          Tone.getDraw().schedule(() => {
            // timeline ?

            // console.log('ticks : ' + Tone.getTransport().ticks)
            // console.log('PPQ : ' + Tone.getTransport().PPQ)
            // console.log('TimeSig : ' + Tone.getTransport().timeSignature)
            // console.log('SampleTime : ' + Tone.getTransport().sampleTime)
            // console.log('BlockTime : ' + Tone.getTransport().blockTime)
            // this.onDivisionUpdate();

            // Need to put different event for that ?
            // this.emit('setCurrentDivision', this.divisions?.[event.divisionId])
            this.emit('onTimerChangeLowRate', event.divisionId)
          }, time);
        }
        else if (event.type === 'high_rate_refresh') {
          Tone.getDraw().schedule(() => {
            // Maybe put that with an event
            this.state.timer = event.divisionId;
            this.emit('setCurrentDivision', this.divisions?.[event.divisionId])
            this.emit('onTimerChangeHighRate', event.divisionId)
          }, time);
        }
        else if (event.type === 'meta' && event.metaEvent === 'end') {
          const loopParameters = playerConfig.state.loopParameters
          if (loopParameters.isActive) {
            if ((event.divisionId >= (loopParameters.playableContent.temporalIndicators.endDivision - 1)) && loopParameters.playableContent.playableType === event.playableType) {
              Tone.getTransport().pause(time + 0.01);
              Tone.getDraw().schedule(() => {
                setTimeout(() => {
                  this.emit('pause', true, division.divisionId);
                  // TODO: keep for mini player
                  this.emit('loopEnd');
                }, this.state.divisionPerBeat * this.divisionDuration)
              }, time)
            }
          }
          else if (event.divisionId >= (this.playableContent!.temporalIndicators.endDivision - 1)) {
            Tone.getTransport().pause(time + 0.01);
            Tone.getDraw().schedule(() => {
              setTimeout(() => {
                this.emit('pause', false, division.divisionId);
                this.emit('setTimer', this.playableContent!.temporalIndicators.startDivision, true, false);
              }, this.state.divisionPerBeat * this.divisionDuration)
            }, time)
          }
        }
        else if (event.type === 'countdown_stop') {
          this.state.playerStatus = 'play'
          this.cancelCountdown()
        }
      })
    }
    catch (error) {
      this.triggerError(error, 'playPartDivision()')
    }
  }

  public resetPlayableContent() {
    this.stop();
    this.tonePart?.dispose();
    this.tonePart = null
    this.state.baseTempo = defaultAudioPlayerState.baseTempo
    this.state.beatPerMeasure = defaultAudioPlayerState.beatPerMeasure
    this.state.currentDivision = defaultAudioPlayerState.currentDivision
    this.state.divisionDuration = defaultAudioPlayerState.divisionDuration
    this.state.divisionPerBeat = defaultAudioPlayerState.divisionPerBeat
    this.state.playerStatus = defaultAudioPlayerState.playerStatus
    this.state.tempo = defaultAudioPlayerState.tempo
    this.state.timer = defaultAudioPlayerState.timer
    this.playableContent = null;
    this.divisions = null
    this.countdownValue = 4
    this.countdownEvent = null
    this.divisionOffset = 0
    this.state.error = false
  }

  // TODO: handle pick up measures (should be embed in division ?)
  /**
   * low_rate_refresh -> Event to send main thread update that could be done less frequently than each division. Could be good for intensive tasks
   * high_rate_refresh -> Event to send main thread update that should be done each division.
   *
   * Following audio events could be condensed in one event if we need to simplify it (if it helps in terms of performance)
   * metronome -> Event to play metronome sound
   * audio -> Event to play synth (melody, piano left, piano right)
   *
   * meta -> More global and ponctual event that could trigger anything (like start, end)
   *
   * @param divisions
   * @param playableContent
   * @private
   */
  private computePartArray(divisions: Division[], playableContent: AbstractPlayableContent): TimelinePartEvents[] {
    const parts: TimelinePartEvents[] = [];
    const metaEvents = this.computeMetaEventsForTimeline(playableContent);

    if (playableContent instanceof RhythmicBloc) {
      metaEvents.push({
        divisionId: divisions[divisions.length - 1].id,
        metaEvent: 'end',
        playableType: 'bloc',
        type: 'meta'
      })
    }

    divisions.forEach((division) => {
      const divisionEvents: TimelinePartEvent[] = [];

      if (!this.state.currentDivision && division.id >= 0) {
        this.emit('setCurrentDivision', division)
      }

      const lowRateRefreshRate = playableContent.temporalIndicators.divisionPerBeat * playableContent.temporalIndicators.beatPerMeasure;
      if (division.id % lowRateRefreshRate === 0) {
        divisionEvents.push({
          divisionId: division.id,
          type: 'low_rate_refresh',
        });
      }
      divisionEvents.push({
        divisionId: division.id,
        type: 'high_rate_refresh',
      });

      /* Audio Tasks */
      // TODO: might use schedule repeat from Tone.JS instead since always at the same rate.
      if (division.id % playableContent.temporalIndicators.divisionPerBeat === 0) {
        divisionEvents.push({
          divisionId: division.id,
          type: 'metronome',
        });
      }
      if (division.melodyNote && (division.melodyNote.noteStatus === 4 || division.melodyNote.noteStatus === 2)) {
        const melodyNote = division.melodyNote;
        if (melodyNote.noteStatus === 4 || melodyNote.noteStatus === 2) {
          divisionEvents.push({
            divisionId: division.id,
            type: 'audio',
            key: [division.melodyNote.key],
            durations: [this.computeTime(division.melodyNote.duration)],
            voice: 'melody',
          });
        }
      }
      ['LEFT', 'RIGHT'].forEach((hand) => {
        const notes = division.currentHandprint.filter((note) => note.hand === hand && (note.noteStatus === 4 || note.noteStatus === 2));
        if (notes.length > 0) {
          const keys = notes.map((note) => note.key);
          const durations = notes.map((note) => this.computeTime(note.duration));

          divisionEvents.push({
            divisionId: division.id,
            type: 'audio',
            key: keys,
            durations: durations,
            voice: ('piano_' + hand.toLowerCase()) as AudioTimelinePartEventVoices,
          })
        }
      })

      // Meta Events
      const divisionMetaEvents = metaEvents.filter((metaEvent) => metaEvent.divisionId === division.id);
      divisionEvents.push(...divisionMetaEvents);

      const timeInTicks = this.computeTime(division.id + this.divisionOffset);

      parts.push({
        divisionId: division.id,
        events: divisionEvents,
        time: timeInTicks
      });
    });

    return parts;
  }
  private computeMetaEventsForTimeline(playableContent: AbstractPlayableContent, parentStartDivision = 0): MetaTimelinePartEvent[] {
    const metaEvents: MetaTimelinePartEvent[] = [];
    const actualStartDivision = parentStartDivision + playableContent.temporalIndicators.startDivision;
    const actualEndDivision = parentStartDivision + playableContent.temporalIndicators.endDivision - 1

    if (playableContent.playableType === 'version') {
      metaEvents.push({
        divisionId: actualStartDivision,
        type: 'meta',
        metaEvent: 'start',
        playableType: 'list',
      });

      metaEvents.push({
        divisionId: actualEndDivision,
        type: 'meta',
        metaEvent: 'end',
        playableType: 'list',
      });
    }

    metaEvents.push({
      divisionId: actualStartDivision,
      type: 'meta',
      metaEvent: 'start',
      playableType: playableContent.playableType,
    });
    if (playableContent.playableTimeline.length > 0) {
      playableContent.playableTimeline.forEach((childPlayableContent) => {
        metaEvents.push(...this.computeMetaEventsForTimeline(childPlayableContent, actualStartDivision));
      })
    }
    metaEvents.push({
      divisionId: actualEndDivision,
      type: 'meta',
      metaEvent: 'end',
      playableType: playableContent.playableType,
    });

    return metaEvents;
  }

  /**
   * Computes the time in ticks for a given division ID.
   * This function converts our custom division-based timing into Tone.js ticks,
   * ensuring precise event scheduling regardless of time signature or tempo.
   *
   * @param {number} divisionId - The ID of the division to compute the time for.
   * @returns {string} The time in ticks as a string (e.g., '480i'), which Tone.js interprets as ticks.
  */
  private computeTime(divisionId: number) {
    // Example to keep to understand difference between our divisionPerBeat/beatPerMeasure and actual one from tone and from music in general
    // In our notation a "beat" could be 3 divisions (ternary) or 4 divisions (binary)
    // In our notation a "division" could be a 8t note une croche (ternary) or a 16t note une double (binary)
    // So in out notation a beat in ternary is 3 * 8t (soit 1.5 noire) and in binary is 4 * 16t = 2 * 8t (soit 1 noire)
    // In standard notation a beat is a beat meaning it's always 4t (a noire)

    // So we need to map our systeme to the standard one, so :
    //   - in binary, one division = a 16th note | in ternary, one division = an 8th note = 2 * 16th note
    //   - in binary, one of our beat = 1 standard beat | in ternary, one of out beat = 1.5 standard beat


    // for now ternary will always be 3 divisionPerBeat but in the future could be a multiple, in that case it would be more complicated
    // const divisionIdAsSixteenth = this.playableContent!.temporalIndicators.divisionPerBeat === 3 ? (divisionId * 2) : divisionId;
    // const binaryBeatPerMeasure = this.playableContent!.temporalIndicators.divisionPerBeat === 3 ? (this.state.beatPerMeasure * 1.5) : this.state.beatPerMeasure;

    // // Then we should map our sixteenth id to the standard transport notation
    // // '0:0:0' is : bar:quarter:sixteenths, so
    // // - bar range from 0 -> infinity | with one new beat each (4 * binaryBeatPerMeasure) sixteenth
    // // - quarter range from 0 -> (binaryBeatPerMeasure - 1) | with one new bar each 4 sixteenth
    // // - sixteenth range from 0 -> 3
    // // And we should use the rest of bar division to get actual quarter and sixteenth for special case where last beat might be only 2 sixteenth (in 9/8 for instance)

    // const bar = Math.floor(divisionIdAsSixteenth / (4 * binaryBeatPerMeasure));       // Measure
    // const inBarSixteenthLeft = divisionIdAsSixteenth % (4 * binaryBeatPerMeasure);       // Left in the measure
    // const quarter = Math.floor(inBarSixteenthLeft / 4) % binaryBeatPerMeasure;        // Noir (eq beat for us)
    // const sixteenth = inBarSixteenthLeft % 4;                                            // Double

    // // console.log(divisionId + ' | ' + divisionIdAsSixteenth + ' | ' + bar+":"+quarter+":"+sixteenth);

    // return (bar+":"+quarter+":"+sixteenth);

    // Full example for Ternary with 2 beats per measure (divisionId to beatQuarterSixteenth)
    // 1 -> 0:0:0 // 2 -> 0:0:2 // 3 -> 0:1:0 // 4 -> 0:1:2 // 5 -> 0:2:0 // 6 -> 0:2:2 // 7 -> 1:0:0

    // Full example for Ternary with 3 beats per measure (divisionId to beatQuarterSixteenth)
    // 1 -> 0:0:0 // 2 -> 0:0:2 // 3 -> 0:1:0 // 4 -> 0:1:2 // 5 -> 0:2:0 // 6 -> 0:2:2 // 7 -> 0:3:0 // 8 -> 0:3:2
    // 9 -> 0:4:0 -> here we skip 0:4:2 because it doesn't exist in 9/8 since we have 3.5 beat per measure
    // 10 -> 1:0:0

    // Full example for Binary with 2 beats per measure (divisionId to beatQuarterSixteenth)
    // 1 -> 0:0:0 // 2 -> 0:0:1 // 3 -> 0:0:2 // 4 -> 0:0:3 // 5 -> 0:1:0 // 6 -> 0:1:1 // 7 -> 0:1:2 // 8 -> 0:1:3 // 9 -> 0:2:0

    const ticksPerDivision = (Tone.getTransport().PPQ * this.state.beatPerMeasure) / (this.state.beatPerMeasure * this.state.divisionPerBeat);
    const timeInTicks = divisionId * ticksPerDivision;
    return `${timeInTicks}i`; // The 'i' denotes ticks in Tone.js
  }

  /* Main Timer Function */

  get timerFlat() {
    return Math.max(this.state.timer, 0);
  }

  private computeDivisions(_divisions: Division[], playableContent: AbstractPlayableContent, withCountdownDivisions = true) {
    let divisions: Division[] = []
    if (withCountdownDivisions) {
      this.divisionOffset = playableContent.temporalIndicators.divisionPerBeat * playableContent.temporalIndicators.beatPerMeasure;
      const countdownDivisions: Division[] = []
      for (let index = 0; index < this.divisionOffset; index++) {
        const division = new Division()
        division.id = -(index + 1)
        countdownDivisions.push(division)
      }

      divisions = [...countdownDivisions.reverse(), ..._divisions]
    }
    else {
      this.divisionOffset = 0
      divisions = _divisions
    }

    this.divisions = divisions.reduce((acc, division) => {
      acc[division.id] = division
      return acc
    }, {} as Record<number, Division>)

    return divisions
  }

  private cancelCountdown() {
    this.isFirstCountdownTick = true
    this.setCountdown(this.playableContent?.temporalIndicators.beatPerMeasure || 4)
    if (this.countdownEvent) {
      const time = this.computeTime(this.countdownEvent.divisionId + this.divisionOffset)
      const timelinePartEvents = this.tonePart?.at(time) as Tone.ToneEvent<TimelinePartEvents>
      if (timelinePartEvents) {
        timelinePartEvents.value.events = timelinePartEvents.value?.events?.filter((el) => el.type !== 'countdown_stop') || []
        this.tonePart?.at(time, timelinePartEvents.value)
        this.countdownEvent = null
      }
    }
  }

  private async play(withCountdown = true) {
    if (playerModals.state.activeModals.length > 0) {
      return
    }
    try {
      this.clearTimeouts();

      if (Tone.getContext().state !== 'running') {
        await Tone.getContext().resume();
      }

      if (this.tonePart?.state === 'stopped') {
        Tone.getTransport().seconds = 0;
        this.tonePart.start()
      }

      if (!localStorage.getItem("disableCountdown") && withCountdown) {
        this.playWithCountDown();
      }
      else {
        Tone.getTransport().start();
        this.state.playerStatus = 'play';
      }
    } catch (error) {
      this.triggerError(error, 'play()')
    }
  }

  private startCountdownTimeout: NodeJS.Timeout
  private playWithCountDown() {
    try {
      this.state.playerStatus = 'countdown';
      const divisionCountdownEnded = this.state.timer
      const divisionCountdownStarted = divisionCountdownEnded - (this.state.divisionPerBeat * this.state.beatPerMeasure)
      this.countdownEvent = {
        divisionId: divisionCountdownEnded,
        type: 'countdown_stop'
      }
      const time = this.computeTime(divisionCountdownEnded + this.divisionOffset)
      const timelinePartEvents = this.tonePart?.at(time) as Tone.ToneEvent<TimelinePartEvents>
      if (timelinePartEvents) {
        if (!timelinePartEvents.value || !Array.isArray(timelinePartEvents.value.events)) {
          timelinePartEvents.value.events = []
        }
        timelinePartEvents.value.events.unshift(this.countdownEvent)
        this.tonePart?.at(time, timelinePartEvents.value)

        this.emit('setTimer', divisionCountdownStarted);

        this.startCountdownTimeout = setTimeout(() => {
          Tone.getTransport().start();
        }, this.divisionDuration * 2)

        setTimeout(() => { // Set a timeout to compute first division timing to sync audio recording
          Audio.musicTranscriptionTimer.computeDivisionStartAt();
        }, this.state.divisionPerBeat * this.state.beatPerMeasure * this.state.divisionDuration)
      }
      else if (this.state.timer >= (playerConfig.state.loopParameters.playableContent.temporalIndicators.endDivision ?? this.playableContent?.temporalIndicators.endDivision)) {
        this.state.playerStatus = 'pause'
        this.setTimerToLoopStart()
        this.restartFromLoop()
      }
    }
    catch (error) {
      this.triggerError(error, 'playWithCountDown()')
    }
  }

  private pause() {
    Tone.getTransport().pause();
    this.clearTimeouts();
    this.isFirstCountdownTick = true

    if (this.state.playerStatus === 'countdown') {
      this.emit('setTimer', this.countdownEvent?.divisionId, true, false);
      this.cancelCountdown()
    }

    this.state.playerStatus = 'pause';
  }
  private stop() {
    Tone.getTransport().stop();
    this.clearTimeouts();
    this.cancelCountdown();

    this.state.playerStatus = 'pause';
    this.setTimer(0);
  }
  public stopPart() {
    if (this.tonePart?.state === 'started') {
      this.tonePart?.stop()
    }
  }

  private setTimer(time: number, snap: boolean = false, withOffset = true) {
    let actualTime = time;
    if (snap) {
      actualTime = Math.floor(time / this.state.divisionPerBeat) * this.state.divisionPerBeat;
    }

    // console.log('setTimer : ' + time + ' -> ' + actualTime + ' (snap : ' + snap + ')');
    Tone.getTransport().position = this.computeTime(actualTime + (withOffset ? this.divisionOffset : 0));
    this.state.timer = actualTime;
    this.emit('onTimerChangeLowRate', actualTime, withOffset)
    this.emit('onTimerChangeHighRate', actualTime, withOffset)
    this.emit('setCurrentDivision', this.divisions?.[actualTime])
  }
  setTimerToLoopStart() {
    this.emit('setTimer', playerConfig.state.loopParameters.playableContent.temporalIndicators.startDivision, true, false);
  }
  private setCountdown(value: number) {
    this.countdownValue = value
    this.emit('countdown', this.countdownValue)
  }

  private delayBeforeStartTimeout: any = -1;
  private delayBeforeStartDuration: number = 0;
  readonly RESTART_DELAY_MS = 1000;
  private restartFromLoop(delay = this.RESTART_DELAY_MS) {
    this.pause();

    this.state.playerStatus = 'player_controlled';

    // Added timeout to prevent lookahead
    setTimeout(() => {
      this.emit('setTimer', playerConfig.state.loopParameters.playableContent.temporalIndicators.startDivision, true, false);
    }, LookAHeadInMilliseconds);

    // Delay start
    this.delayBeforeStartDuration = delay;
    this.delayBeforeStartTimeout = setTimeout(() => {
      this.delayBeforeStartTimeout = 0;
      this.play();
    }, delay);
  }

  increaseLoopRestartDelayIfNeeded(delay: number) {
    if (this.delayBeforeStartDuration > 0 && this.delayBeforeStartDuration < delay) {
      this.clearTimeouts();
      this.state.playerStatus = 'pause';
      this.delayBeforeStartTimeout = setTimeout(() => {
        this.delayBeforeStartTimeout = 0;
        if (playerConfig.state.playerView === 'piano' || playerConfig.state.playerView === 'partition') {
          this.play()
        }
      }, delay);
    }
  }

  private clearTimeouts() {
    clearTimeout(this.delayBeforeStartTimeout);
    clearTimeout(this.startCountdownTimeout)
  }

  /* Speed & Time Signature */

  get baseTempo() {
    return this.state.baseTempo
  }
  get tempo() {
    return this.state.tempo
  }

  private initTempo(tempo: number, speed: number) {
    this.state.baseTempo = tempo;
    this.setTempoFromSpeed(speed);
  }
  public changeTempoUsingRatio(ratio: number) {
    this.setTempo(this.baseTempo * ratio);
  }
  public setTempo(tempo: number, withSetSpeed = true) {
    Tone.getTransport().bpm.value = tempo;
    this.state.tempo = tempo;
    this.computeDurations(withSetSpeed)
  }

  get divisionDuration() {
    return this.state.divisionDuration
  }
  get divisionDurationInSeconds() {
    return this.divisionDuration / SecondInMiliseconds
  }

  private setTempoFromSpeed(value: number) {
    this.setTempo(value * this.baseTempo, false);
  }
  private computeDurations(withSetSpeed = true) {
    if (withSetSpeed) {
      playerConfig.setSpeed(this.tempo / this.baseTempo, false)
    }
    const beatDuration = SecondInMiliseconds / ((playerConfig.state.speed * this.baseTempo) / MinuteInSeconds)
    this.state.divisionDuration = beatDuration / this.state.divisionPerBeat
  }

  public triggerError(error: any, method: string) {
    analyticsController.logBasicAnalyticsEvent('audio player error on play part', 'player', {
      error: error.message,
      method
    })

    Vue.prototype.Monitoring?.captureException(error)

    this.state.error = true
    this.state.isLoading = false
  }
}

const audioPlayer = new AudioPlayer();

export default audioPlayer;
